import _ from 'lodash';
import React, { useCallback, useContext, useState, useEffect } from 'react';

import { BreakpointTrackerContext } from '@jutro/layout'
import { TranslatorContext } from '@jutro/locale';
import { useModal } from '@jutro/components';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { useDataRefresh, ViewModelServiceContext, ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { messages as commonMessages } from '@xengage/gw-platform-translations';

import { AgencyAdminService } from "twia-capability-agencyadmin";
import { AgentRegistrationService } from '../../../../capabilities/ent-capability-agentregistration/index.js';
import metadata from './UserActivation.metadata.json5';
import styles from './UserActivation.module.scss';
import messages from './UserActivation.messages.js'

function UserActivationPage(props) {
  const { id } = props;
  const breakpoint = useContext(BreakpointTrackerContext);
  const [showErrors, setShowErrors] = useState(false);
  const [loading, setShowLoading] = useState(true);
  const [isExpired, setIsExpired] = useState(false); // DE18937 Wait for backend to load the value.
  const { showAlert } = useModal();
  const translator = useContext(TranslatorContext);
  const { onValidate, isComponentValid } = useValidation(id);
  const viewModelService = useContext(ViewModelServiceContext);
  const [userSearchVM, setUserSearchVM] = useState();
  const { refreshData } = useDataRefresh();

  useEffect(() => {
    if (viewModelService && !userSearchVM) {
        const searchParams = new URL(window.location.href).searchParams;
        const token = searchParams && searchParams.get('token');
        AgencyAdminService.getActivationDTO(token).then(activation => {
            const vmInitial = {
                "isAgencyVerification": activation.isAgencyVerification,
                "registrationToken": token 
            };
            const dto = 'org.tfpa.edge.capabilities.agent.dto.UserRegisterSearchDTO';
            const viewModel = viewModelService.create(vmInitial, 'pc', dto);
            setUserSearchVM(viewModel);
            // US26505 if registration is expired, notify user
            if (token) {
                AgentRegistrationService.isExpired(token
                ).then(response => {
                    setIsExpired(response);
                    setShowLoading(false);
                }).catch(err => {
                    setShowLoading(false);
                });
            } else {
                setShowLoading(false);
            }
        });
    }
  }, [viewModelService]);

  const writeValue = useCallback((value, path) => {
        _.set(userSearchVM, `${path}.value`, value);
        setUserSearchVM(userSearchVM);
        refreshData();
    },
    [userSearchVM, setUserSearchVM, refreshData]
  );

  const createActivation = useCallback(() => {
    if (!isComponentValid) {
        setShowErrors(true);
        return false;
    }

    setShowLoading(true);
    AgencyAdminService.createActivationLink(userSearchVM.value
    ).then(response => {
      window.location.href = response;
    }).catch(err => {
        const errorCode = _.get(err, 'data.appErrorCode');
        const body = errorCode === 5220 ? 
            messages.activationLockoutBody : messages.activationFailedBody;
        showAlert({
            title: messages.activationFailedTitle,
            message: body,
            status: 'error',
            icon: 'mi-error-outline',
            confirmButtonText: commonMessages.ok,
        }).catch(_.noop);
        setShowLoading(false);
    });
  }, [isComponentValid, userSearchVM]);

  const overrideProps = {
    '@field': {
      labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
      showErrors,
      showRequired: true
    },
    activationInstructions: {
        visible: !isExpired
    },
    activationContainer: {
        visible: !loading && !isExpired
    },
    loadingIndicator: {
        visible: loading
    },
    userLastName: {
        visible: _.get(userSearchVM, 'isAgencyVerification.value') === false
    },
    agencyZipCode: {
        visible: _.get(userSearchVM, 'isAgencyVerification.value') === true
    },
    agencyContactLastName: {
        visible: _.get(userSearchVM, 'isAgencyVerification.value') === true
    },
    activationExpired: {
        visible: isExpired
    }
  };

  const resolvers = {
    resolveClassNameMap: styles,
    resolveCallbackMap: {
        createActivation: createActivation
    }
  }

  return (
    <ViewModelForm
      uiProps={metadata.pageContent}
      model={userSearchVM}
      overrideProps={overrideProps}
      classNameMap={resolvers.resolveClassNameMap}
      callbackMap={resolvers.resolveCallbackMap}
      componentMap={resolvers.resolveComponentMap}
      onValidationChange={onValidate}
      onValueChange={writeValue}
    />
  )
}

export default UserActivationPage;
